/** Phase 8 Slice 4.2 — Tenant-quality sparkline.
 *
 * Tiny SVG line chart with hover tooltip. No new chart library.
 *
 * Props:
 *   data: [{ date: "YYYY-MM-DD", overall_score: 0..1, run_count: int }]
 *   height (px, default 56), label (string, optional)
 */
import { useState } from "react";

export default function QualitySparkline({ data, height = 56, label }) {
  const [hover, setHover] = useState(null);
  if (!Array.isArray(data) || data.length === 0) {
    return (
      <div className="text-xs text-muted-foreground"
           data-testid="quality-sparkline-empty">
        {label || "No daily data yet."}
      </div>
    );
  }
  const W = 480;        // logical width
  const H = height;
  const PAD = 4;
  const xs = data.map((_, i) =>
    PAD + (i * (W - 2 * PAD)) / Math.max(data.length - 1, 1));
  const ys = data.map((d) => {
    const v = Math.max(0, Math.min(1, d.overall_score ?? 0));
    return PAD + (1 - v) * (H - 2 * PAD);
  });
  const path = xs.map((x, i) =>
    `${i === 0 ? "M" : "L"}${x.toFixed(1)},${ys[i].toFixed(1)}`).join(" ");
  const lineColor = (() => {
    const avg = data.reduce((s, d) => s + (d.overall_score || 0), 0) / data.length;
    if (avg >= 0.8) return "stroke-emerald-500";
    if (avg >= 0.6) return "stroke-amber-500";
    return "stroke-rose-500";
  })();

  return (
    <div className="w-full" data-testid="quality-sparkline">
      {label && (
        <div className="text-[11px] uppercase tracking-wide text-muted-foreground mb-1">
          {label}
        </div>
      )}
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-auto"
           preserveAspectRatio="none"
           onMouseLeave={() => setHover(null)}>
        {/* baseline at 0.5 */}
        <line x1={PAD} x2={W - PAD}
              y1={PAD + 0.5 * (H - 2 * PAD)}
              y2={PAD + 0.5 * (H - 2 * PAD)}
              className="stroke-muted/40" strokeDasharray="2 3" />
        <path d={path} fill="none" strokeWidth="1.5" className={lineColor} />
        {xs.map((x, i) => (
          <circle key={i} cx={x} cy={ys[i]} r="6"
                  className="fill-transparent"
                  data-testid={`quality-sparkline-pt-${data[i].date}`}
                  onMouseEnter={() => setHover(i)} />
        ))}
        {hover != null && (
          <>
            <circle cx={xs[hover]} cy={ys[hover]} r="2.5"
                    className={`${lineColor} fill-current`} />
          </>
        )}
      </svg>
      <div className="flex justify-between text-[10px] text-muted-foreground mt-1">
        <span data-testid="quality-sparkline-first">
          {data[0].date}
        </span>
        <span data-testid="quality-sparkline-last">
          {data[data.length - 1].date}
        </span>
      </div>
      {hover != null && (
        <div className="text-[11px] text-foreground mt-1"
             data-testid="quality-sparkline-tooltip">
          <span className="font-mono">{data[hover].date}</span>
          {" · "}
          <span>avg <strong>{(data[hover].overall_score ?? 0).toFixed(2)}</strong></span>
          {" · "}
          <span>{data[hover].run_count} runs</span>
        </div>
      )}
    </div>
  );
}
